import React, { useEffect, useMemo, useRef, useState } from "react";
import config from "../../config";
import getChangeColumnDefs from "../../utils/Change/changecolumnDefs";
import getRmaColumnDefs from "../../utils/Rma/rmaColumnDefs";
import getKnowledgeColumnDefs from "../../utils/KnowledgeBase/knowledgeBaseColumnDefs";
import getProblemColumnDefs from "../../utils/Problem/problemColumnDefs";
import getIncidentColumnDefs from "../../utils/Incident/incidentColumnDefs";

import { ChangeData } from "../../utils/Change/changeTypes";
import getTaskColumnDefs from "../../utils/Task/taskColumnDefs";
import { TaskData } from "../../utils/Task/taskTypes";
import Grid from "../Grid/Grid";
import TaskModal from "../Modal/TaskModal";
import { RmaData } from "../../utils/Rma/rmaTypes";
import { KnowledgeBaseData } from "../../utils/KnowledgeBase/KnowledgeBaseTypes";
import { ProblemData } from "../../utils/Problem/problemTypes";
import { IncidentData } from "../../utils/Incident/incidentTypes";
import { authFetch } from "../../utils/Security/authFetch";
import { loadLookupIncidents, loadLookupTasks } from "../../utils/lookupData";

type TaskGridLookupProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedItems?: (
      | TaskData
      | ChangeData
      | RmaData
      | KnowledgeBaseData
      | ProblemData
      | IncidentData
      )[];
  onSelect: (
      tasks: (
          | TaskData
          | ChangeData
          | RmaData
          | KnowledgeBaseData
          | ProblemData
          | IncidentData
          )[]
  ) => void;
};

const TaskGridLookup: React.FC<TaskGridLookupProps> = ({
                                                         isOpen,
                                                         onClose,
                                                         onSelect,
                                                         selectedItems = [],
                                                       }) => {
  const [activeTab, setActiveTab] = useState<
      "task" | "change" | "rma" | "knowledge" | "problem" | "incident"
  >("task");
  const isForPetrobras =
      localStorage.getItem("isForPetrobras") === "true";
  const [taskData, setTaskData] = useState<TaskData[]>([]);
  const [incidentData, setIncidentData] = useState<IncidentData[]>([]);
  const [changeData, setChangeData] = useState<ChangeData[]>([]);
  const [rmaData, setRmaData] = useState<RmaData[]>([]);
  const [knowledgeData, setKnowledgeData] = useState<KnowledgeBaseData[]>([]);
  const [problemdata, setProblemData] = useState<ProblemData[]>([]);

  const taskColumnDefs = useMemo(
      () =>
          getTaskColumnDefs(
              () => {},
              () => {}
          ),
      []
  );
  const incidentColumnDefs = useMemo(
      () =>
          getIncidentColumnDefs(
              () => {},
              () => {}
          ),
      []
  );
  const changeColumnDefs = useMemo(
      () =>
          getChangeColumnDefs(
              () => {},
              () => {}
          ),
      []
  );
  const rmaColumnDefs = useMemo(
      () =>
          getRmaColumnDefs(
              () => {},
              () => {}
          ),
      []
  );
  const knowledgeColumnDefs = useMemo(
      () =>
          getKnowledgeColumnDefs(
              () => {},
              () => {}
          ),
      []
  );
  const problemColumnDefs = useMemo(
      () =>
          getProblemColumnDefs(
              () => {},
              () => {}
          ),
      []
  );

  const [loading, setLoading] = useState(false);
  const gridApiRef = useRef<any>(null);

  const [selectedRows, setSelectedRows] = useState<{
    task: TaskData[];
    change: ChangeData[];
    rma: RmaData[];
    knowledge: KnowledgeBaseData[];
    problem: ProblemData[];
    incident: IncidentData[];
  }>({
    task: [],
    change: [],
    rma: [],
    knowledge: [],
    problem: [],
    incident: [],
  });

  useEffect(() => {
    if (isOpen) {
      const initialSelected = {
        task: [] as TaskData[],
        change: [] as ChangeData[],
        rma: [] as RmaData[],
        knowledge: [] as KnowledgeBaseData[],
        problem: [] as ProblemData[],
        incident: [] as IncidentData[],
      };

      selectedItems.forEach((item) => {
        const typedItem = item as unknown as { source?: string };
        // Rows picked in the grid carry no source: infer the tab from the ticketId prefix
        const id = String(item?.ticketId ?? "");
        const source =
            typedItem.source ??
            (id.startsWith("INC") || id.startsWith("IND")
                ? "incident"
                : id.startsWith("CHN")
                    ? "change"
                    : id.startsWith("TSK")
                        ? "task"
                        : id.startsWith("PRB")
                            ? "problem"
                            : id.startsWith("RMA")
                                ? "rma"
                                : "knowledge");

        switch (source) {
          case "task":
            initialSelected.task.push(item as TaskData);
            break;
          case "change":
            initialSelected.change.push(item as ChangeData);
            break;
          case "rma":
            initialSelected.rma.push(item as RmaData);
            break;
          case "knowledge":
            initialSelected.knowledge.push(item as KnowledgeBaseData);
            break;
          case "problem":
            initialSelected.problem.push(item as ProblemData);
            break;
          case "incident":
            initialSelected.incident.push(item as IncidentData);
            break;
          default:
        }
      });
      setSelectedRows(initialSelected);
    }
    // Seed once per open from the already-related tickets
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    // Stop a load for a closed lookup / previous tab from writing into state
    let cancelled = false;
    const isCancelled = () => cancelled;

    const fetchData = async () => {
      const userId = localStorage.getItem("userid");
      const isAdmin = localStorage.getItem("isAdmin") === "true";

      let url = "";
      switch (activeTab) {
        case "task":
          // Same loading as TaskView: cached list first, then light batches
          await loadLookupTasks(setTaskData, isCancelled);
          break;
        case "change":
          url = isAdmin
              ? `${config.apiBaseUrl}/api/changes`
              : `${config.apiBaseUrl}/api/changes/user/${userId}`;
          const changeRes = await authFetch(url);
          setChangeData(await changeRes.json());
          break;
        case "problem":
          url = isAdmin
              ? `${config.apiBaseUrl}/api/problems`
              : `${config.apiBaseUrl}/api/problems/user/${userId}`;
          const probRes = await authFetch(url);
          setProblemData(await probRes.json());
          break;
        case "incident":
          // Same loading as IncidentView: cached list, else 2000-row batches
          await loadLookupIncidents(setIncidentData, isCancelled);
          break;

        case "knowledge":
          url = isAdmin
              ? `${config.apiBaseUrl}/api/knowledgeBases`
              : `${config.apiBaseUrl}/api/knowledgeBases/user/${userId}`;
          const knowRes = await authFetch(url);
          setKnowledgeData(await knowRes.json());
          break;
        case "rma":
          url = isAdmin
              ? `${config.apiBaseUrl}/api/rmas`
              : `${config.apiBaseUrl}/api/rmas/user/${userId}`;
          const rmaRes = await authFetch(url);
          setRmaData(await rmaRes.json());
          break;
      }
    };

    setLoading(true);
    fetchData()
        .catch((e) => console.error(`Error loading ${activeTab}:`, e))
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    return () => {
      cancelled = true;
    };
  }, [isOpen, activeTab]);

  const handleSelectionChange = (
      rows: (
          | TaskData
          | ChangeData
          | RmaData
          | KnowledgeBaseData
          | ProblemData
          | IncidentData
          )[]
  ) => {
    // The grid only reports loaded rows: keep picks that haven't loaded yet
    const tabRows: { ticketId: any }[] = {
      task: taskData,
      change: changeData,
      rma: rmaData,
      knowledge: knowledgeData,
      problem: problemdata,
      incident: incidentData,
    }[activeTab];
    const loadedIds = new Set(tabRows.map((r) => String(r.ticketId)));
    setSelectedRows((prev) => ({
      ...prev,
      [activeTab]: [
        ...(prev[activeTab] as { ticketId: any }[]).filter(
            (item) => !loadedIds.has(String(item.ticketId)),
        ),
        ...rows,
      ],
    }));
  };

  // Tick already-related rows as task/incident batches arrive
  type Tab = typeof activeTab;
  const tabData: Record<Tab, { ticketId: any }[]> = {
    task: taskData,
    change: changeData,
    rma: rmaData,
    knowledge: knowledgeData,
    problem: problemdata,
    incident: incidentData,
  };
  const tabColumnDefs: Record<Tab, any> = {
    task: taskColumnDefs,
    change: changeColumnDefs,
    rma: rmaColumnDefs,
    knowledge: knowledgeColumnDefs,
    problem: problemColumnDefs,
    incident: incidentColumnDefs,
  };
  const tabs: { key: Tab; label: string }[] = [
    { key: "task", label: "Tasks" },
    ...(isForPetrobras
        ? []
        : ([
          { key: "change", label: "Changes" },
          { key: "rma", label: "RMA" },
          { key: "knowledge", label: "Knowledge Base" },
          { key: "problem", label: "Problems" },
        ] as { key: Tab; label: string }[])),
    { key: "incident", label: "Incidents" },
  ];

  // Tick already-related rows; re-runs as task/incident batches arrive
  const preselectRows = (params: { api: any }) => {
    const ids = new Set(
        (selectedRows[activeTab] as { ticketId: any }[]).map((item) =>
            String(item.ticketId),
        ),
    );
    params.api.forEachNode((node: any) => {
      if (
          node.data &&
          !node.isSelected() &&
          ids.has(String(node.data.ticketId))
      ) {
        node.setSelected(true);
      }
    });
  };

  const allSelected = (Object.keys(selectedRows) as Tab[]).flatMap((tab) =>
      (selectedRows[tab] as { ticketId: any }[]).map((item) => ({
        tab,
        ticketId: String(item.ticketId),
      })),
  );

  // Remove a chip: untick it in the grid if it's showing, else drop it directly
  const removeSelected = (tab: Tab, ticketId: string) => {
    const node =
        tab === activeTab ? gridApiRef.current?.getRowNode(ticketId) : null;
    if (node?.isSelected()) {
      node.setSelected(false);
      return;
    }
    setSelectedRows((prev) => ({
      ...prev,
      [tab]: (prev[tab] as { ticketId: any }[]).filter(
          (item) => String(item.ticketId) !== ticketId,
      ),
    }));
  };

  const handleSubmit = () => {
    onSelect(
        (Object.keys(selectedRows) as Tab[]).flatMap(
            (tab) => selectedRows[tab] as any[],
        ) as any,
    );
    onClose();
  };

  const count = allSelected.length;

  return (
      <TaskModal
          isOpen={isOpen}
          onClose={onClose}
          title="Relate Tickets"
          subtitle="Tick the tickets to link from any tab, then confirm."
          footer={
            <>
              <span className="lk-status" style={{ marginRight: "auto" }}>
                {count} selected
              </span>
              <button type="button" className="lk-btn" onClick={onClose}>
                Cancel
              </button>
              <button
                  type="button"
                  className="lk-btn lk-btn-primary"
                  onClick={handleSubmit}
              >
                {count
                    ? `Relate ${count} ticket${count > 1 ? "s" : ""}`
                    : "Save"}
              </button>
            </>
          }
      >
        <div className="lk-tabs" role="tablist">
          {tabs.map((tab) => (
              <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  className={`lk-tab ${activeTab === tab.key ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
          ))}
        </div>

        <div className="lk-selected">
          <span className="lk-selected-label">Selected</span>
          {count === 0 ? (
              <span className="lk-selected-empty">No tickets selected yet</span>
          ) : (
              allSelected.map(({ tab, ticketId }) => (
                  <span key={`${tab}-${ticketId}`} className="lk-chip">
                {ticketId}
                    <button
                        type="button"
                        aria-label={`Remove ${ticketId}`}
                        onClick={() => removeSelected(tab, ticketId)}
                    >
                  ×
                </button>
              </span>
              ))
          )}
        </div>

        {loading && tabData[activeTab].length === 0 && (
            <div className="lk-status">Loading…</div>
        )}
        <div className="lk-grid">
          <Grid<any>
              key={activeTab} // force remount on tab change
              rowData={tabData[activeTab]}
              columnDefs={tabColumnDefs[activeTab]}
              rowSelection="multiple"
              height="58vh"
              getRowId={(params: { data: { ticketId: any } }) =>
                  String(params.data.ticketId)
              }
              onGridReady={(params) => {
                gridApiRef.current = params.api;
              }}
              onFirstDataRendered={preselectRows}
              onModelUpdated={preselectRows}
              onSelectionChanged={handleSelectionChange}
          />
        </div>
      </TaskModal>
  );
};

export default TaskGridLookup;
